import { identifyApiErrorCode } from '@/utils/axios';
import {
    CircleCircle,
    CircleMergedConfig,
    CustomAxiosError,
} from '@calm/evexia-web-sdk';
import createCircleApi from '@rhg/libs/api/create-circle-api';
import useAuth from '@rhg/libs/auth0/use-auth';
import {
    queryClient,
    queryKeys,
    skipToken,
    useMutation,
    useQuery,
} from '@rhg/libs/react-query';
import { AxiosError } from 'axios';
import { useActiveCircle } from '../use-active-circle';

export const DEMO_PARTNER_INVALID_ERROR_CODE = 'demo_partner_invalid';

const mostRecentSponsorshipQueryOptions = {
    queryKey: function (recipientId?: number) {
        return [queryKeys.mostRecentSponsorship, recipientId];
    },
    queryFn: async function ({
        recipientId,
        circleAuthorizationToken,
        getAccessTokenSilently,
    }: {
        recipientId: number;
        circleAuthorizationToken: string;
        getAccessTokenSilently: () => Promise<string>;
    }) {
        const circleApi = createCircleApi(getAccessTokenSilently);

        const response = await circleApi
            .getMostRecentSponsorship({
                recipientId,
                circleAuthorizationToken,
            })
            .catch((e: CustomAxiosError) => {
                if (e?.response?.status === 404) {
                    return { data: null };
                } else {
                    throw e;
                }
            });
        return response.data;
    },
    queryOptions: function ({
        recipientId,
        isAuthenticated,
        isSponsored,
        circleAuthorizationToken,
    }: {
        recipientId?: number;
        isAuthenticated: boolean;
        isSponsored: boolean;
        circleAuthorizationToken: string | undefined;
    }) {
        return {
            retry: false,
            enabled: Boolean(
                isAuthenticated &&
                    !!recipientId &&
                    isSponsored &&
                    circleAuthorizationToken
            ),
        };
    },
};

export function useMostRecentSponsorship() {
    const { activeCircle } = useActiveCircle();
    const recipientId = activeCircle?.recipient.id;
    const circleAuthorizationToken = activeCircle?.authorizationJWT;
    const { isAuthenticated, getAccessTokenSilently } = useAuth();
    const isSponsored = Boolean(activeCircle?.sponsor);
    const { queryKey, queryFn, queryOptions } =
        mostRecentSponsorshipQueryOptions;
    const options = queryOptions({
        recipientId,
        isAuthenticated,
        isSponsored,
        circleAuthorizationToken,
    });

    const { data, error, ...otherProps } = useQuery<
        CircleMergedConfig | null | undefined,
        AxiosError
    >({
        queryKey: queryKey(recipientId!),
        queryFn: circleAuthorizationToken
            ? async () =>
                  queryFn({
                      recipientId: recipientId!,
                      getAccessTokenSilently,
                      circleAuthorizationToken,
                  })
            : skipToken,
        ...options,
    });

    const mostRecentSponsorship = data;

    const hasLostSponsorship =
        activeCircle?.hasLostSponsorship || error?.response?.status === 403;

    const errorCode = error ? identifyApiErrorCode(error) : null;
    const isDemoSponsor = Boolean(
        mostRecentSponsorship?.isDemoSponsor ||
            (errorCode && errorCode === DEMO_PARTNER_INVALID_ERROR_CODE)
    );

    return {
        hasLostSponsorship,
        isDemoSponsor,
        mostRecentSponsorship,
        isEnabled: options.enabled,
        error,
        ...otherProps,
    };
}
export function useGetMostRecentSponsorship() {
    const { queryKey, queryFn } = mostRecentSponsorshipQueryOptions;
    const { getAccessTokenSilently } = useAuth();

    const { mutate, mutateAsync, ...otherProps } = useMutation({
        mutationFn: async (circle: CircleCircle) => {
            if (circle?.authorizationJWT) {
                const response = await queryFn({
                    recipientId: circle.recipient.id,
                    getAccessTokenSilently,
                    circleAuthorizationToken: circle.authorizationJWT!,
                });
                queryClient.setQueryData<CircleMergedConfig | null>(
                    queryKey(circle.recipient.id),
                    response
                );
                return response;
            }
            return null;
        },
    });

    return {
        getMostRecentSponsorship: mutate,
        getMostRecentSponsorshipAsync: mutateAsync,
        ...otherProps,
    };
}
